/**
 * @fileoverview Client-side producer for the webhook outbox.
 * Inserts events into the `webhook_outbox` table for the server-side drainer
 * (Supabase Edge Function) to deliver reliably with HMAC signing and retries.
 */

import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';

/**
 * Enqueue a webhook event into the outbox table.
 * The Edge Function drainer will pick it up and deliver.
 *
 * @param {Object} params
 * @param {string} params.userId - Authenticated user's UUID (must match auth.uid())
 * @param {string} params.eventType - Domain event type (e.g., 'task.updated')
 * @param {Record<string, any>} params.payload - JSON payload to deliver
 * @param {string=} params.endpoint - Optional HTTP endpoint; if provided, stored at payload_json.endpoint
 * @param {string=} params.entityRef - Optional entity reference string (e.g., 'task:123')
 * @param {string=} params.idempotencyKey - Optional idempotency key; if omitted, we will generate one
 * @param {Record<string, any>=} params.meta - Optional metadata for observability
 * @returns {Promise<{ id: string }|null>}
 */
export async function enqueueWebhook({
  userId,
  eventType,
  payload,
  entityRef,
  idempotencyKey,
  meta = {},
  endpoint,
}) {
  try {
    if (!userId || !eventType) {
      throw new Error('userId and eventType are required');
    }

    // Align with DB schema: public.webhook_outbox(user_id, entity_ref, payload_json, idempotency_key, ...)
    const payload_json = { eventType, payload, meta, ...(endpoint ? { endpoint } : {}) };
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('webhook_outbox')
      .insert([
        {
          user_id: userId,
          entity_ref: entityRef || null,
          payload_json,
          idempotency_key: idempotencyKey || cryptoRandomId(),
          status: 'pending',
          attempt_count: 0,
          next_attempt_at: nowIso,
        },
      ])
      .select('id')
      .single();

    if (error) throw error;
    return data || null;
  } catch (e) {
    logger.error('enqueueWebhook failed', e);
    return null;
  }
}

function cryptoRandomId() {
  try {
    // Browser crypto random 16 bytes hex
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return Array.from(a)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (_) {
    return String(Date.now()) + Math.random().toString(16).slice(2);
  }
}

export default { enqueueWebhook };
